/**
 * Holds an invitation across the sign-in / sign-up detour.
 *
 * Accepting a MEMBER invitation requires an account. When an unauthenticated
 * recipient clicks accept we send them through auth first, which means the token
 * has to survive a handful of navigations (and, for social sign-in, a full
 * round trip out to Google or Apple and back).
 *
 * `sessionStorage`, not `localStorage`, on purpose: the stash is scoped to the
 * tab and dies when it closes. An invitation token is a bearer credential —
 * leaving one in `localStorage` on a shared computer, indefinitely, would be a
 * real exposure. Losing it when the tab closes is not much of a loss, since the
 * link in the email still works.
 */
const STORAGE_KEY = "tripbears.pendingInvitation";

export type PendingInvitation = {
  token: string;
  action: "accept" | "decline";
};

export function stashPendingInvitation(pending: PendingInvitation): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage can be unavailable (Safari private mode, storage quota, a
    // hardened browser). The flow still works from the email link, so this is
    // a degradation rather than a failure — the user just re-clicks the link
    // after signing in.
  }
}

export function readPendingInvitation(): PendingInvitation | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "token" in parsed &&
      "action" in parsed &&
      typeof (parsed as PendingInvitation).token === "string" &&
      ((parsed as PendingInvitation).action === "accept" ||
        (parsed as PendingInvitation).action === "decline")
    ) {
      return parsed as PendingInvitation;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearPendingInvitation(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See stashPendingInvitation.
  }
}
